import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { SectionBox } from '@/components';
import { useAppSelector } from '@/redux/hooks';
import {
  complianceStatsSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import { useDispatch } from 'react-redux';
import { complianceStatsActions } from '@/redux/actions';

import { statsDemoData } from './Stats.constants';
import styles from './Stats.module.sass';

type StatsItemType = {
  title: string;
  value: number | undefined;
};

const Stats = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: customerStats, loading } = useAppSelector(
    complianceStatsSelectors.getComplianceStatsSlice,
  );
  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);

  useEffect(() => {
    if (!isShowDemoData) {
      dispatch(complianceStatsActions.fetchCustomerStats());
    }
  }, [isShowDemoData]);

  const data = useMemo(() => {
    return isShowDemoData ? statsDemoData : customerStats;
  }, [isShowDemoData, customerStats]);

  const statsData = useMemo((): StatsItemType[] => {
    return [
      {
        title: t('translation.total'),
        value: data?.totals?.byLevel || 0,
      },
      {
        title: t('translation.risk_explanation_level_low'),
        value: data?.byLevel?.low || 0,
      },
      {
        title: t('translation.risk_explanation_level_middle'),
        value: data?.byLevel?.medium || 0,
      },
      {
        title: t('translation.risk_explanation_level_high'),
        value: data?.byLevel?.high || 0,
      },
      {
        title: t('translation.severe_risk'),
        value: data?.byLevel?.severe || 0,
      },
    ];
  }, [customerStats]);

  return (
    <SectionBox>
      {{
        title: t('translation.customers_overview'),
        body: (
          <div className={styles.stats}>
            {statsData.map((item) => (
              <div key={item.title} className={styles.item}>
                <div className={styles.title}>{item.title}</div>
                <NumericFormat
                  value={item.value}
                  displayType="text"
                  thousandSeparator=" "
                  className={styles.value}
                />
              </div>
            ))}
          </div>
        ),
      }}
    </SectionBox>
  );
};

export default Stats;
